import React, { useState, useEffect } from 'react';
import { useHasChangedAfterFirstLoad } from 'Lib/Hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'Data/Redux';
import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { actions } from 'Data/ReduxViewModels/EquipmentInterfacing';
import { equipmentTypes } from '..';
import { isWebSerialApiSupported } from 'Lib/WebSerialApi/helpers';
import { hasNonEmptyValue } from 'Lib/Any';

export type InterfaceConnectionPromptIssuerProps = {
  equipment?: any;
  equipmentType: string;
  disabled?: boolean;
};

/**
 * Component that issues the connection prompt when an equipment item changes.
 * Nothing is rendered.
 */
const InterfaceConnectionPromptIssuer: React.FC<InterfaceConnectionPromptIssuerProps> = ({
  equipment,
  equipmentType,
  disabled,
}) => {
  const hasChanged = useHasChangedAfterFirstLoad(equipment?.Id);
  const dispatch = useDispatch();

  const isConnected: boolean = useSelector(
    (state: RootState) => state.equipmentInterfacing.equipment[equipment?.Id]?.isConnected
  );
  const isConnecting: boolean = useSelector(
    (state: RootState) => state.equipmentInterfacing.equipment[equipment?.Id]?.connect.isInProgress
  );
  // We need to delay the issuing of the prompt until we have loaded the equipment's port settings
  const [awaitingPortSettingsLoad, setAwaitingPortSettingsLoad] = useState(false);
  useEffect(() => {
    if (hasNonEmptyValue(equipment?.PortSettings) && awaitingPortSettingsLoad) {
      if (
        equipment?.PortSettings?.IsValidPortSettings &&
        equipment?.Driver &&
        !disabled &&
        isWebSerialApiSupported() &&
        !(isConnected || isConnecting)
      ) {
        dispatch(
          noticeActions.addNotice({
            id: `interfaceConnectionRequest-${equipment?.Id}`,
            message: `${equipmentTypes[equipmentType].name} ${equipment?.Code} supports interfacing.`,
            actionLink: {
              linkText: 'Connect',
              action: actions.requestPort({
                equipmentId: equipment?.Id,
                options: {
                  baudRate: equipment?.PortSettings?.BaudRate,
                  dataBits: equipment?.PortSettings?.DataBits,
                  stopBits: equipment?.PortSettings?.StopBits,
                  parity: equipment?.PortSettings?.Parity?.toLowerCase(),
                },
                equipmentData: {
                  type: equipmentType,
                  code: equipment?.Code,
                  expectedComPort: equipment?.PortSettings?.ConnectedComPort,
                },
              }),
            },
          })
        );
      }
      setAwaitingPortSettingsLoad(false);
    }
  }, [
    awaitingPortSettingsLoad,
    isConnected,
    isConnecting,
    dispatch,
    equipment,
    equipment?.PortSettings,
    equipmentType,
    disabled,
  ]);

  useEffect(() => {
    if (hasChanged) setAwaitingPortSettingsLoad(true);
  }, [hasChanged]);

  return null;
};

export { InterfaceConnectionPromptIssuer };
export default InterfaceConnectionPromptIssuer;
